import React from "react";
import { Row, Col, Divider, Button, Image } from "antd";
import {
  EyeOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import { GET_LOAD_IMAGE_NEW_BY } from "../../../../API/apiUrls";
import { getDocLabel, getFileType } from "../utils";
import PdfPreviewModal from "../components/PdfPreviewModal";

const UploadImageTab = ({
  fileListByContractCourt,
  token,
  pdfPreviewOpen,
  pdfPreviewUrl,
  setPdfPreviewOpen,
  setPdfPreviewUrl,
  setIsModalAddImge,
}) => {
  // ── sort: menuType ASC → imageType ASC ───────────────────────
  const sorted = [...fileListByContractCourt].sort((a, b) =>
    a.menuType !== b.menuType
      ? a.menuType - b.menuType
      : a.imageType - b.imageType,
  );

  // ── แบ่ง Zone ตาม menuType ────────────────────────────────────
  const zone5 = sorted.filter((f) => f.menuType === 5);
  const zone6 = sorted.filter((f) => f.menuType === 6);
  const zone8 = sorted.filter((f) => f.menuType === 8);
  const zone9 = sorted.filter((f) => f.menuType === 9);
  const zone10 = sorted.filter((f) => f.menuType === 10);

  const ZONE_CONFIG = {
    5: {
      title: "เอกสารคำฟ้อง",
      color: "#d46b08",
      bg: "#fff7e6",
      border: "#ffd591",
    },
    6: {
      title: "เอกสารชั้นศาล",
      color: "#096dd9",
      bg: "#e6f4ff",
      border: "#91caff",
    },
    8: {
      title: "เอกสารปิดบัญชี",
      color: "#389e0d",
      bg: "#f6ffed",
      border: "#b7eb8f",
    },
    9: {
      title: "เอกสารชั้นบังคับคดี",
      color: "#531dab",
      bg: "#f9f0ff",
      border: "#d3adf7",
    },
    10: {
      title: "เอกสารรีไฟแนนซ์",
      color: "#08979c",
      bg: "#e6fffb",
      border: "#87e8de",
    },
  };

  // ── card รูปภาพ ───────────────────────────────────────────────
  const renderImageCard = (file) => {
    const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
    const { label, color } = getDocLabel(file.menuType, file.imageType);

    return (
      <Col key={file.imageId} xs={8} sm={6} md={4}>
        <div
          style={{
            border: `1px solid ${color}`,
            borderRadius: 8,
            padding: 8,
            textAlign: "center",
            background: "#fff",
          }}
        >
          <Image
            src={previewUrl}
            alt={file.path}
            style={{
              width: "100%",
              height: 80,
              objectFit: "cover",
              borderRadius: 4,
            }}
            preview={{
              mask: (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <EyeOutlined style={{ fontSize: 14 }} />
                  <span style={{ fontSize: 10 }}>ดูรูป</span>
                </div>
              ),
            }}
          />
          <br />
          <div
            style={{
              marginTop: 6,
              background: color,
              color: "#fff",
              fontSize: 11,
              borderRadius: 4,
              padding: "1px 6px",
              display: "inline-block",
            }}
          >
            {label}
          </div>
        </div>
      </Col>
    );
  };

  // ── card PDF ──────────────────────────────────────────────────
  const renderPdfCard = (file) => {
    const previewUrl = `${GET_LOAD_IMAGE_NEW_BY}/${file.path}?token=${token}`;
    const { label, color } = getDocLabel(file.menuType, file.imageType);
    return (
      <Col key={file.imageId} xs={8} sm={6} md={4}>
        <div
          style={{
            border: `1px solid ${color}`,
            borderRadius: 8,
            padding: 8,
            textAlign: "center",
            background: "#fff",
          }}
        >
          <div
            onClick={() => {
              setPdfPreviewUrl(previewUrl);
              setPdfPreviewOpen(true);
            }}
            style={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: 80,
              background: "#fff1f0",
              borderRadius: 6,
              border: "1px dashed #ff4d4f",
              gap: 4,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            <FilePdfOutlined style={{ fontSize: 32, color: "#ff4d4f" }} />
            <span style={{ fontSize: 11, color: "#ff4d4f", fontWeight: 500 }}>
              <EyeOutlined style={{ marginRight: 4 }} />
              ดู PDF
            </span>
          </div>
          <div
            style={{
              marginTop: 6,
              background: color,
              color: "#fff",
              fontSize: 11,
              borderRadius: 4,
              padding: "1px 6px",
              display: "inline-block",
            }}
          >
            {label}
          </div>
        </div>
      </Col>
    );
  };

  // ── render Zone ───────────────────────────────────────────────
  const renderZone = (files, menuType) => {
    if (!files.length) return null;
    const cfg = ZONE_CONFIG[menuType];
    const pdfFiles = files.filter((f) => getFileType(f.path) === "pdf");
    const imgFiles = files.filter((f) => getFileType(f.path) === "image");

    return (
      <div
        key={menuType}
        style={{
          border: `1px solid ${cfg.border}`,
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          background: cfg.bg,
        }}
      >
        {/* Zone Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            paddingBottom: 8,
            borderBottom: `1px solid ${cfg.border}`,
          }}
        >
          <div
            style={{
              background: cfg.color,
              color: "#fff",
              borderRadius: 6,
              padding: "2px 12px",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {cfg.title}
          </div>
          <span style={{ fontSize: 14, color: "#888" }}>
            {files.length} ไฟล์
          </span>
        </div>

        {/* รูปภาพ */}
        {imgFiles.length > 0 && (
          <>
            <div style={{ fontSize: 14, color: "#888", marginBottom: 6 }}>
              รูปภาพ ({imgFiles.length})
            </div>
            <Image.PreviewGroup>
              <Row
                gutter={[8, 8]}
                style={{ marginBottom: pdfFiles.length ? 12 : 0 }}
              >
                {imgFiles.map(renderImageCard)}
              </Row>
            </Image.PreviewGroup>
          </>
        )}

        {/* PDF */}
        {pdfFiles.length > 0 && (
          <>
            <div
              style={{
                fontSize: 14,
                color: "#888",
                marginBottom: 6,
                marginTop: imgFiles.length ? 4 : 0,
              }}
            >
              <FilePdfOutlined style={{ color: "#ff4d4f", marginRight: 4 }} />
              เอกสาร PDF ({pdfFiles.length})
            </div>
            <Row gutter={[8, 8]}>{pdfFiles.map(renderPdfCard)}</Row>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <Button
        variant="outlined"
        color="blue"
        onClick={() => {
          setIsModalAddImge(true);
        }}
      >
        อัพโหลด รูป/ไฟล์+
      </Button>
      <Divider orientation="center">
        <FileSearchOutlined /> ดูเอกสารทนาย
      </Divider>

      {renderZone(zone5, 5)}
      {renderZone(zone6, 6)}
      {renderZone(zone8, 8)}
      {renderZone(zone9, 9)}
      {renderZone(zone10, 10)}

      <PdfPreviewModal
        open={pdfPreviewOpen}
        url={pdfPreviewUrl}
        onClose={() => {
          setPdfPreviewOpen(false);
          setPdfPreviewUrl("");
        }}
      />
    </>
  );
};

export default UploadImageTab;
